/**
 * Bounded Context Query Service
 * 
 * Application service for bounded context read operations following CQRS pattern.
 * Optimized for data retrieval and presentation.
 */

import { injectable, inject } from 'inversify';
import { BoundedContext } from '../../domain/entities/BoundedContext';
import type { IBoundedContextRepository } from '../../../meta-model-design/domain/ports/IBoundedContextRepository';
import type { IDomainRepository } from '../../../meta-model-design/domain/ports/IDomainRepository';
import { BoundedContextDto } from '../queries/GetBoundedContextQuery';
import { IPaginatedResult } from '@swoft/core';
import { DDD_SYMBOLS } from '../../../../infrastructure/di/DDD_SYMBOLS';

export interface BoundedContextListOptions {
  page?: number;
  pageSize?: number;
  domainId?: string;
  type?: 'core' | 'supporting' | 'generic';
  searchTerm?: string;
}

@injectable()
export class BoundedContextQueryService {
  constructor(
    @inject(DDD_SYMBOLS.IBoundedContextRepository) private boundedContextRepository: IBoundedContextRepository,
    @inject(DDD_SYMBOLS.IDomainRepository) private domainRepository: IDomainRepository
  ) {}

  async getBoundedContext(domainId: string, contextId: string): Promise<BoundedContextDto | null> {
    const boundedContext = await this.boundedContextRepository.findById(contextId);
    if (!boundedContext) {
      return null;
    }

    // Verify the context belongs to the specified domain
    if (boundedContext.domainId !== domainId) {
      throw new Error(`Bounded context ${contextId} does not belong to domain ${domainId}`);
    }

    return await this.toDto(boundedContext);
  }

  async listBoundedContexts(options: BoundedContextListOptions = {}): Promise<IPaginatedResult<BoundedContextDto>> {
    const page = options.page || 1;
    const pageSize = options.pageSize || 10;

    let result;
    if (options.domainId) {
      result = await this.boundedContextRepository.findByDomainId(options.domainId, {
        page,
        pageSize,
        type: options.type,
        searchTerm: options.searchTerm
      });
    } else {
      result = await this.boundedContextRepository.findAll({
        page,
        pageSize,
        type: options.type,
        searchTerm: options.searchTerm
      });
    }

    const totalPages = Math.ceil(result.totalCount / pageSize);

    const contextDtos: BoundedContextDto[] = await Promise.all(
      result.contexts.map(context => this.toDto(context))
    );

    return {
      items: contextDtos,
      totalCount: result.totalCount,
      page,
      pageSize,
      totalPages,
      hasNextPage: page < totalPages,
      hasPreviousPage: page > 1
    };
  }

  async searchBoundedContexts(searchTerm: string, domainId: string): Promise<BoundedContextDto[]> {
    const contexts = await this.boundedContextRepository.findByName(domainId, searchTerm);
    return contexts ? [await this.toDto(contexts)] : [];
  }

  private async toDto(boundedContext: BoundedContext): Promise<BoundedContextDto> {
    // Lookup domain name - following Eric Evans' DDD principles for aggregate relationships
    let domainName = 'Unknown Domain';
    try {
      const domain = await this.domainRepository.findById(boundedContext.domainId);
      if (domain) {
        domainName = domain.name;
      }
    } catch (error) {
      console.warn(`Failed to lookup domain ${boundedContext.domainId}:`, error);
    }

    return {
      id: boundedContext.id,
      boundedContextId: boundedContext.id, // Use domain ID as business identifier
      domainId: boundedContext.domainId,
      domainName, // Include the resolved domain name
      name: boundedContext.name,
      description: boundedContext.description,
      type: boundedContext.type,
      ubiquitousLanguage: boundedContext.ubiquitousLanguage,
      domainServices: boundedContext.domainServices || [],
      aggregates: boundedContext.aggregates || [],
      valueObjects: boundedContext.valueObjects || [],
      entities: boundedContext.entities || [],
      createdAt: boundedContext.createdAt,
      updatedAt: boundedContext.updatedAt
    };
  }
}